import { useMemo, useState } from "react";
import {
  Button,
  Dropdown,
  Field,
  Input,
  Link,
  MessageBar,
  MessageBarBody,
  Option,
  Textarea,
} from "@fluentui/react-components";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Activity, ContextBlock, Decision, InputField } from "../types";
import { parseInAppLink, requestInAppNav } from "../../shared/inAppLinks";
import { FEEDBACK_MAX_CHARS, charCount, maxCharsFor, type CharCount } from "../inputLimits";
import styles from "./ApprovalsPanel.module.css";

/** Small trailing chevron for navigation links (design: "Open in My Queue ›"). */
function ChevronRight() {
  return (
    <svg className={styles.navChevron} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M6 3.5 10.5 8 6 12.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CheckMark() {
  return (
    <svg className={styles.btnIcon} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M3 8.5 6.5 12 13 4.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** "matter_intake" → "Matter Intake" for the skill badge (CSS uppercases it). */
export function skillBadgeLabel(skillKey: string): string {
  return skillKey
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Options that confirm or complete the step take the single primary button;
 *  otherwise the first option does, as before. */
const AFFIRMATIVE = new Set(["approve", "approved", "done", "mark as done", "complete", "completed", "submit", "accept", "confirm", "ok", "yes"]);

/** A gate whose answers are approve / reject (as opposed to a Notify card's
 *  authored buttons). Drives the section it sits in and its button layout. */
export function isApprovalActivity(activity: Pick<Activity, "options">): boolean {
  return activity.options.some((o) => ["approve", "reject"].includes(String(o).trim().toLowerCase()));
}

const SMALL_WORDS = new Set(["a", "an", "and", "as", "at", "by", "for", "in", "of", "on", "or", "the", "to", "&"]);

/** Button labels are Title Case whatever the skill sent ("approve" → "Approve",
 *  "mark as done" → "Mark as Done"): small words stay lower except the first. */
export function titleCase(label: string): string {
  return label
    .trim()
    .split(/\s+/)
    .map((w, i) => {
      if (w.length === 0) return w;
      if (i > 0 && SMALL_WORDS.has(w.toLowerCase())) return w.toLowerCase();
      return w[0]!.toUpperCase() + w.slice(1);
    })
    .join(" ");
}

export function primaryOptionIndex(options: readonly string[]): number {
  const i = options.findIndex((o) => AFFIRMATIVE.has(o.trim().toLowerCase()) || /^submit\b/i.test(o.trim()));
  return Math.max(i, 0);
}

/** Stable key for a field's value in the collected `inputs`. */
const fieldKey = (field: InputField, index: number) => field.key ?? field.label ?? `field_${index}`;

/** Field validation state for a counter: error past the limit, neutral while counting, none otherwise. */
function counterState(count: CharCount): "error" | "none" | undefined {
  if (count.over > 0) return "error";
  return count.label ? "none" : undefined;
}

/** One context block → its Fluent rendering, switched on `kind`. */
function ContextBlockView({ block }: { readonly block: ContextBlock }) {
  switch (block.kind) {
    case "summary":
      return (
        <div className={styles.summary}>
          <Markdown remarkPlugins={[remarkGfm]}>{block.text ?? ""}</Markdown>
        </div>
      );
    case "facts":
      return (
        <dl className={styles.facts}>
          {(block.items ?? []).map((item) => (
            <div key={`${item.label ?? ""}=${item.value ?? ""}`} style={{ display: "contents" }}>
              <dt className={styles.factLabel}>{item.label}</dt>
              <dd className={styles.factValue} style={{ margin: 0 }}>
                {item.value}
              </dd>
            </div>
          ))}
        </dl>
      );
    case "draft":
      return (
        <div className={styles.draft}>
          {block.title && <p className={styles.draftTitle}>{block.title}</p>}
          {block.content}
        </div>
      );
    case "links":
      return (
        <div className={styles.links}>
          {(block.items ?? []).map((item) => {
            const inApp = parseInAppLink(item.url);
            return inApp ? (
              <Link key={item.url ?? item.label} as="button" className={styles.navLink} onClick={() => requestInAppNav(inApp)}>
                {item.label ?? item.url}
                <ChevronRight />
              </Link>
            ) : (
              <Link key={item.url ?? item.label} className={styles.navLink} href={item.url} target="_blank" rel="noreferrer">
                {item.label ?? item.url}
                <ChevronRight />
              </Link>
            );
          })}
        </div>
      );
    case "note":
      return (
        <MessageBar intent={block.severity === "warning" ? "warning" : "info"}>
          <MessageBarBody>{block.text}</MessageBarBody>
        </MessageBar>
      );
    default:
      return null;
  }
}

/** One input field → its Fluent control. Native input types cover number/date/
 *  email/file without pulling in extra packages; select uses a Dropdown. */
function InputFieldControl({
  field,
  value,
  disabled,
  onChange,
}: {
  readonly field: InputField;
  readonly value: unknown;
  readonly disabled: boolean;
  readonly onChange: (v: unknown) => void;
}) {
  // Form values are primitives (strings from the controls); guard so a stray
  // object renders as its JSON, never the useless "[object Object]".
  let str: string;
  if (value == null) {
    str = "";
  } else if (typeof value === "object") {
    str = JSON.stringify(value);
  } else {
    str = String(value as string | number | boolean | bigint);
  }
  let control;
  if (field.type === "textarea") {
    control = (
      <Textarea
        value={str}
        placeholder={field.placeholder}
        disabled={disabled}
        onChange={(_, d) => onChange(d.value)}
      />
    );
  } else if (field.type === "select") {
    control = (
      <Dropdown
        placeholder={field.placeholder ?? "Select…"}
        disabled={disabled}
        value={str}
        selectedOptions={str ? [str] : []}
        onOptionSelect={(_, d) => onChange(d.optionValue)}
      >
        {(field.options ?? []).map((opt) => (
          <Option key={opt} value={opt}>
            {opt}
          </Option>
        ))}
      </Dropdown>
    );
  } else if (field.type === "file") {
    // Fluent's Input has no type="file"; capture the chosen file's name only
    // (v1 collects a reference, not the binary).
    control = (
      <input
        type="file"
        disabled={disabled}
        onChange={(e) => onChange(e.target.files?.[0]?.name ?? "")}
      />
    );
  } else {
    // text | number | date | email → Fluent Input with a native input type
    const type = (["number", "date", "email"].includes(field.type) ? field.type : "text") as
      | "text"
      | "number"
      | "date"
      | "email";
    control = (
      <Input
        type={type}
        value={str}
        placeholder={field.placeholder}
        disabled={disabled}
        onChange={(_, d) => onChange(d.value)}
      />
    );
  }
  // Counter near the limit, error past it. Typing is never cut off silently:
  // the message says by how much, and the decision buttons stay disabled.
  const count = charCount(str.length, maxCharsFor(field));
  return (
    <Field
      label={field.label}
      required={field.required}
      hint={field.type === "file" ? "Captures the file reference only." : undefined}
      validationState={counterState(count)}
      validationMessage={count.label ?? undefined}
    >
      {control}
    </Field>
  );
}

interface ActivityCardProps {
  readonly activity: Activity;
  readonly submitting: boolean;
  readonly onSubmit: (decision: Decision) => void;
  /** The option the user already chose. When set the card is read-only and
   *  shows the outcome instead of the form and decision buttons. */
  readonly decision?: string;
}

/** Outcome label for an answered card: approve/reject use the Actions tab's
 *  status vocabulary; any other (open-ended) answer is shown verbatim. */
export function decisionLabel(decision: string): string {
  const d = decision.trim().toLowerCase();
  if (d === "approve") return "Approved";
  if (d === "reject") return "Declined";
  return decision;
}

const STATUS_LABEL = { pending: "Pending", approved: "Approved", declined: "Declined" } as const;

/** Badge text for the card's state: pending until answered, then the outcome. */
export function statusBadge(decision: string | undefined): "pending" | "approved" | "declined" {
  if (decision === undefined) return "pending";
  return decision.trim().toLowerCase() === "reject" ? "declined" : "approved";
}

/** A single pending decision: the prompt, a rich context panel, an adaptive
 *  input form, an optional feedback box, and the decision controls (one button
 *  per option, or a single Submit for open-ended gates). */
export function ActivityCard({ activity, submitting, onSubmit, decision }: ActivityCardProps) {
  const [feedback, setFeedback] = useState("");
  const [values, setValues] = useState<Record<string, unknown>>({});

  const fields = activity.input_fields ?? [];
  const context = activity.context ?? [];
  const approval = isApprovalActivity(activity);
  const statusBadgeClass = styles[`badge-${statusBadge(decision)}`] ?? "";
  const openEnded = activity.options.length === 0;
  const options = openEnded ? [] : activity.options.map(String);

  const hasValue = (v: unknown): boolean => {
    if (v == null) return false;
    // Only primitives are real answers; an object is treated as empty rather
    // than coerced to "[object Object]".
    if (typeof v === "object") return false;
    const primitive = v as string | number | boolean | bigint;
    return String(primitive).trim() !== "";
  };
  const formValid = useMemo(
    () => fields.every((f, i) => !f.required || hasValue(values[fieldKey(f, i)])),
    [fields, values],
  );

  const collectInputs = () =>
    fields.reduce<Record<string, unknown>>((acc, f, i) => {
      const key = fieldKey(f, i);
      if (hasValue(values[key])) acc[key] = values[key];
      return acc;
    }, {});

  const submit = (decision: string) =>
    onSubmit({
      decision,
      feedback: feedback.trim() || undefined,
      inputs: fields.length > 0 ? collectInputs() : undefined,
    });

  const overLimit = useMemo(
    () =>
      fields.some((f, i) => {
        const v = values[fieldKey(f, i)];
        return typeof v === "string" && charCount(v.length, maxCharsFor(f)).over > 0;
      }) || feedback.length > FEEDBACK_MAX_CHARS,
    [fields, values, feedback],
  );
  const feedbackCount = charCount(feedback.length, FEEDBACK_MAX_CHARS);

  const disabled = submitting || !formValid || overLimit;
  // One primary button per card: the confirming option sits last and wide,
  // the others are secondary and come first (design: Reassign | Mark as Done).
  const primaryIndex = primaryOptionIndex(options);
  const orderedOptions = [
    ...options.filter((_, i) => i !== primaryIndex).map((option) => ({ option, primary: false })),
    ...options.filter((_, i) => i === primaryIndex).map((option) => ({ option, primary: true })),
  ];

  return (
    <li className={styles.card}>
      <div className={styles.cardHead}>
        <div className={styles.badges}>
          <span className={`${styles.badge} ${styles.badgeSkill}`}>{skillBadgeLabel(activity.skill_key)}</span>
          {!(approval && decision === undefined) && (
            <span className={`${styles.badge} ${statusBadgeClass}`}>
              {STATUS_LABEL[statusBadge(decision)]}
            </span>
          )}
        </div>
        <p className={styles.prompt}>{activity.prompt}</p>
      </div>

      {context.length > 0 && (
        <div className={styles.context}>
          {context.map((block) => (
            <ContextBlockView
              key={`${block.kind}:${block.title ?? block.text ?? block.content ?? ""}`}
              block={block}
            />
          ))}
        </div>
      )}

      {decision === undefined ? (
        <>
          {fields.length > 0 && (
            <div className={styles.form}>
              {fields.map((field, i) => (
                <InputFieldControl
                  key={fieldKey(field, i)}
                  field={field}
                  value={values[fieldKey(field, i)]}
                  disabled={submitting}
                  onChange={(v) => setValues((prev) => ({ ...prev, [fieldKey(field, i)]: v }))}
                />
              ))}
            </div>
          )}

          <Field
            className={styles.feedback}
            label={<span className={styles.feedbackLabel}>Feedback</span>}
            validationState={counterState(feedbackCount)}
            validationMessage={feedbackCount.label ?? undefined}
          >
            <Textarea
              className={styles.feedbackBox}
              rows={1}
              resize="none"
              placeholder={approval ? "Optional — explain a rejection or edit" : "Optional — helps AiVA improve routing"}
              value={feedback}
              disabled={submitting}
              onChange={(_, data) => setFeedback(data.value)}
              aria-label="Feedback"
            />
          </Field>

          <div className={`${styles.actions} ${approval ? styles.actionsSplit : styles.actionsAuto}`}>
            {openEnded ? (
              <Button
                appearance="primary"
                className={`${styles.cardBtn} ${styles.primaryBtn}`}
                disabled={disabled}
                onClick={() => submit(feedback.trim() || "submitted")}
              >
                <CheckMark />
                Submit
              </Button>
            ) : (
              orderedOptions.map(({ option, primary }) => (
                <Button
                  key={option}
                  appearance={primary ? "primary" : "secondary"}
                  className={`${styles.cardBtn} ${primary ? styles.primaryBtn : styles.secondaryBtn}`}
                  disabled={disabled}
                  onClick={() => submit(option)}
                >
                  {primary && <CheckMark />}
                  {titleCase(option)}
                </Button>
              ))
            )}
          </div>
        </>
      ) : null}
    </li>
  );
}
